import * as React from 'react';
import React__default from 'react';
import { jsx } from 'react/jsx-runtime';
import _objectSpread from '@babel/runtime-corejs3/helpers/esm/objectSpread2';
import _slicedToArray from '@babel/runtime-corejs3/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime-corejs3/helpers/esm/objectWithoutProperties';
import { useIsMounted } from '@thisisbud/react-utils';
import 'core-js/modules/es.object.to-string.js';
import 'core-js/modules/es.promise.js';
import 'core-js/modules/es.promise.finally.js';
import _asyncToGenerator from '@babel/runtime-corejs3/helpers/esm/asyncToGenerator';
import _regeneratorRuntime from '@babel/runtime-corejs3/regenerator';
import _Promise from '@babel/runtime-corejs3/core-js-stable/promise';
import _setTimeout from '@babel/runtime-corejs3/core-js-stable/set-timeout';

var LoadableCtx = /*#__PURE__*/React.createContext({
  disableDelays: false
});
/**
 * Provides a context for use in Loadable components when initializing components.
 *
 * @param props - Provider props including the context to provide to consumers of the Loadable context
 *
 * @example
 * ```jsx
 * import * as React from 'react'
 * import ReactDOM from 'react-dom'
 * import { createStore } from 'redux'
 * import { Provider } from 'react-redux'
 * import { LoadableContextProvider } from '@thisisbud/react-loadable'
 * import Root from './components
 *
 * const store = createStore({
 *   // Reducers
 * })
 *
 * const loadableCtx = {
 *   store,
 * }
 *
 * ReactDOM.render(
 *   <LoadableContextProvider ctx={loadableCtx}>
 *     <Provider store={store}>
 *       <Root/>
 *     </Provider>
 *   </LoadableContextProvider>
 * )
 * ```
 */

function LoadableContextProvider(props) {
  var children = props.children,
      _props$disableDelays = props.disableDelays,
      disableDelays = _props$disableDelays === void 0 ? false : _props$disableDelays,
      ctx = props.ctx;
  var contextValue = React.useMemo(function () {
    return {
      ctx: ctx,
      disableDelays: disableDelays
    };
  }, [disableDelays, ctx]);
  return /*#__PURE__*/jsx(LoadableCtx.Provider, {
    value: contextValue,
    children: children
  });
}

/**
 * A hook to provide lazy component loading functionality.
 *
 * @param props - The Loadable component props that are required for handling the component to be rendered
 * @returns The loaded component if available, loading state and error if any
 */

function useComponent(props) {
  var Component = props.Component,
      isMounted = props.isMounted,
      loadComponent = props.loadComponent; // eslint-disable-line @typescript-eslint/naming-convention

  var needsLoading = typeof Component === 'undefined' && typeof loadComponent === 'function';
  var component = React.useRef(Component);

  var _React$useState = React.useState(needsLoading),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      loading = _React$useState2[0],
      setLoading = _React$useState2[1];

  var _React$useState3 = React.useState(),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      error = _React$useState4[0],
      setError = _React$useState4[1];

  React.useEffect(function () {
    if (!loading || typeof loadComponent !== 'function') {
      return;
    }

    loadComponent().then(function (module) {
      component.current = module.default;
    }).catch(function (err) {
      if (isMounted.current) {
        setError(err);
      }
    }).finally(function () {
      if (isMounted.current) {
        setLoading(false);
      }
    });
  }, [isMounted, loadComponent, loading, setError, setLoading]);
  return [component.current, loading, error];
}

var useLoadableContext = function useLoadableContext() {
  var loadableContext = React__default.useContext(LoadableCtx);
  return loadableContext;
};

/**
 * A hook to provide initialization functionality.
 *
 * @param props - The Loadable component props that are required for handling the initialization
 * @returns The loaded data if available, loading state and error if any
 */

function useInit(props) {
  var ctx = props.ctx,
      init = props.init,
      isMounted = props.isMounted,
      loadInit = props.loadInit;

  var _useLoadableContext = useLoadableContext(),
      globalCtx = _useLoadableContext.ctx;

  var needsLoading = typeof init === 'function' || typeof loadInit === 'function';

  var _React$useState = React.useState(),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      data = _React$useState2[0],
      setData = _React$useState2[1];

  var _React$useState3 = React.useState(needsLoading),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      loading = _React$useState4[0],
      setLoading = _React$useState4[1];

  var _React$useState5 = React.useState(),
      _React$useState6 = _slicedToArray(_React$useState5, 2),
      error = _React$useState6[0],
      setError = _React$useState6[1];

  React.useEffect(function () {
    if (typeof init === 'undefined' && typeof loadInit === 'undefined') {
      return;
    }

    new _Promise(function (resolve) {
      var combinedCtx = _objectSpread(_objectSpread({}, globalCtx), ctx);

      if (typeof init === 'function') {
        resolve(init(combinedCtx));
      } else {
        resolve(loadInit().then( /*#__PURE__*/function () {
          var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(module) {
            var initFn;
            return _regeneratorRuntime.wrap(function _callee$(_context) {
              while (1) {
                switch (_context.prev = _context.next) {
                  case 0:
                    initFn = module.default;
                    return _context.abrupt("return", _Promise.resolve(initFn(combinedCtx)));

                  case 2:
                  case "end":
                    return _context.stop();
                }
              }
            }, _callee);
          }));

          return function (_x) {
            return _ref.apply(this, arguments);
          };
        }()));
      }
    }).then(function (res) {
      if (isMounted.current) {
        setData(res);
      }
    }).catch(function (err) {
      if (isMounted.current) {
        setError(err);
      }
    }).finally(function () {
      if (isMounted.current) {
        setLoading(false);
      }
    });
  }, [ctx, globalCtx, init, isMounted, loadInit, setError, setLoading]);
  return [data, loading, error];
}

/**
 * A hook to provide a delay for a condition to be met.
 *
 * @param delay - The delay (in milliseconds) to wait for
 * @returns A boolean indicating whether or not the delay is still running
 */

function useDelay(delay, isMounted) {
  var timeout = React.useRef();

  var _React$useState = React.useState(delay !== 0 && typeof window !== 'undefined'),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      running = _React$useState2[0],
      setRunning = _React$useState2[1]; // Only run in browsers


  React.useEffect(function () {
    if (delay === 0) {
      return;
    }

    timeout.current = _setTimeout(function () {
      if (isMounted.current) {
        setRunning(false);
      }
    }, delay);
    return function () {
      clearTimeout(timeout.current);
    };
  }, [delay, isMounted]);
  return running;
}

var _excluded = ["Component", "children", "ctx", "fallback", "fallbackDelay", "fallbackMinDuration", "init", "loadComponent", "loadInit"];
function Loadable(props) {
  var Component = props.Component,
      children = props.children,
      ctx = props.ctx,
      fallback = props.fallback,
      _props$fallbackDelay = props.fallbackDelay,
      fallbackDelay = _props$fallbackDelay === void 0 ? 100 : _props$fallbackDelay,
      _props$fallbackMinDur = props.fallbackMinDuration,
      fallbackMinDuration = _props$fallbackMinDur === void 0 ? 1000 : _props$fallbackMinDur,
      init = props.init,
      loadComponent = props.loadComponent,
      loadInit = props.loadInit,
      otherProps = _objectWithoutProperties(props, _excluded);

  var isMounted = useIsMounted();

  var _useComponent = useComponent({
    Component: Component,
    isMounted: isMounted,
    loadComponent: loadComponent
  }),
      _useComponent2 = _slicedToArray(_useComponent, 3),
      LoadedComponent = _useComponent2[0],
      componentLoading = _useComponent2[1],
      componentError = _useComponent2[2]; // eslint-disable-line @typescript-eslint/naming-convention


  var _useInit = useInit({
    ctx: ctx,
    init: init,
    isMounted: isMounted,
    loadInit: loadInit
  }),
      _useInit2 = _slicedToArray(_useInit, 3),
      data = _useInit2[0],
      dataLoading = _useInit2[1],
      dataError = _useInit2[2];

  var _React$useState = React.useState(componentLoading || dataLoading),
      _React$useState2 = _slicedToArray(_React$useState, 1),
      loadingRequired = _React$useState2[0]; // Only needed for the assertion of the initial loading state


  var hasFallback = typeof fallback !== 'undefined';

  var _useLoadableContext = useLoadableContext(),
      disableDelays = _useLoadableContext.disableDelays;

  var shouldDelayFallback = !Boolean(disableDelays) && hasFallback && loadingRequired;
  var shouldUseMinFallbackDuration = !Boolean(disableDelays) && hasFallback && loadingRequired && fallbackMinDuration !== 0;
  var fallbackDelayed = useDelay(shouldDelayFallback ? fallbackDelay : 0, isMounted);
  var fallbackMinDurationActive = useDelay(shouldUseMinFallbackDuration ? fallbackDelay + fallbackMinDuration : 0, isMounted); // Handle errors by letting them get caught by an error boundary

  if (typeof componentError !== 'undefined') {
    throw componentError;
  } else if (typeof dataError !== 'undefined') {
    throw dataError;
  } // Show fallback element


  if (componentLoading || dataLoading || fallbackMinDurationActive) {
    if (typeof fallback !== 'undefined' && !fallbackDelayed) {
      return fallback;
    }

    return null; // Render component with data and props provided
  } else if (typeof LoadedComponent !== 'undefined') {
    return /*#__PURE__*/jsx(LoadedComponent, _objectSpread(_objectSpread({
      data: data
    }, otherProps), {}, {
      children: children
    })); // eslint-disable-line react/jsx-props-no-spreading
  }

  if (typeof children !== 'undefined') {
    return /*#__PURE__*/jsx(React.Fragment, {
      children: children
    }); // eslint-disable-line react/jsx-no-useless-fragment
  }

  return null;
}

export { LoadableContextProvider, Loadable as default };
